// hooks
import { useRateLimitError } from "frontend/hooks/useRateLimitError";

const getDefaultSuccessMessage = (actionDesc, override) => {
    return override || `${actionDesc} was successful.`;
};
const getDefaultErrorMessage = (actionDesc, override) => {
    return (
        override || `Sorry, we were unable to ${actionDesc}. Please try again.`
    );
};

const isNotEmpty = value => {
    // Check if value is not null and not undefined
    if (value !== null && value !== undefined) {
        // Check if value is not an empty string
        if (typeof value === "string" && value.trim() !== "") {
            return true;
        }
        // Check if value is not an empty array
        if (Array.isArray(value) && value.length > 0) {
            return true;
        }
    }
    return false;
};

export const useApolloGql = ({
    actionDesc = "Make Apollo Gql Api Call",
    apiCall,
    errorHandler,
    successMessageOverride,
    errorMessageOverride
}) => {
    const { isRateLimited } = useRateLimitError();

    // Methods
    const handleError = unrwappedSsResp => {
        let resp = { ...unrwappedSsResp };

        // global Rate Limit error handling
        if (isRateLimited(resp.ssResp)) {
            resp.message =
                "Editing speed limited for security. Please wait a moment and try again.";
        }

        // See if consumer has anything to say about this...
        if (errorHandler) {
            resp = errorHandler(resp);
        }
        console.error(unrwappedSsResp.message, unrwappedSsResp);
        return resp;
    };

    /**
     * extractSsErrors: grab all potential error messages from Server Response
     * @param {Object} ssResp Server Side Response
     * @returns {Array} errors array of string errors that consumer can use to create more meaningful errors.
     */
    const extractSsErrors = ssResp => {
        let errors = [];

        // SSErrors: graphQLErrors or root message
        if (ssResp?.graphQLErrors) {
            ssResp.graphQLErrors.forEach(ssGraphQLError => {
                if (isNotEmpty(ssGraphQLError?.message)) {
                    errors.push(ssGraphQLError.message);
                }
            });
        } else if (ssResp?.message) {
            errors.push(ssResp?.message);
        }

        // SSErrors: Root Errors
        const rootErrors = ssResp?.errors || [];
        rootErrors.forEach(ssRootError => {
            if (isNotEmpty(ssRootError?.message)) {
                errors.push(ssRootError.message);
            }
        });

        // SSErrors: API Data Response Errors
        const data = ssResp?.data || {};
        Object.entries(data).forEach(([key, value]) => {
            // I have not seen anything other than strings on sub data responses thus far
            // Maybe BE sends arrays sometimes, or something else? We should adress if we see it.
            const responseErrors = data[key]?.errors;
            if (isNotEmpty(responseErrors)) {
                if (typeof responseErrors === "string") {
                    errors.push(responseErrors);
                } else {
                    console.error(
                        `extractSsErrors unhandled "errors" type: ${typeof responseErrors}. We should address this error type.`
                    );
                }
            }
        });

        // SSErrors: confirm all ok's returned on respObj's are true
        // sometimes backend sends `ok` as false without `errors` array or sub errors string
        // no need to do this work if we already found errors above
        if (errors.length === 0) {
            let ok = true;
            Object.entries(data).forEach(([key, value]) => {
                const respObj = data[key];
                if (respObj.ok === false) {
                    ok = false;
                }
            });

            if (!ok) {
                // in that case add default error message
                errors.push(
                    getDefaultErrorMessage(actionDesc, errorMessageOverride)
                );
            }
        }

        return errors;
    };

    /**
     * getUnwrappedssResp
     *  - extract all potential errors
     *  - normalize response for Frontend use
     * @param {Object} ssResp Server Side Response
     * @returns normalizedResponse
     */
    const getUnwrappedssResp = (ssResp, gqlVariables) => {
        const errors = extractSsErrors(ssResp);
        const success = errors.length === 0;
        const message = success
            ? getDefaultSuccessMessage(actionDesc, successMessageOverride)
            : getDefaultErrorMessage(actionDesc, errorMessageOverride);

        // Normalized Response Shape
        return {
            actionDesc,
            success,
            message,
            errors,
            ssResp,
            gqlVariables
        };
    };

    const handleApiCall = async gqlVariables => {
        try {
            const ssResp = await apiCall({
                variables: gqlVariables
            });
            const unrwappedSsResp = getUnwrappedssResp(ssResp, gqlVariables);
            return unrwappedSsResp.success
                ? unrwappedSsResp
                : handleError(unrwappedSsResp);
        } catch (ssResp) {
            const unrwappedSsResp = getUnwrappedssResp(ssResp, gqlVariables);
            unrwappedSsResp.message = `Catch Error: ${unrwappedSsResp.message}`;
            return handleError(unrwappedSsResp);
        }
    };

    return {
        apiCall: handleApiCall
    };
};
